import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Account } from '../model/form/account';
import { CognitoService } from './cognito.service';
import { User } from '../model/user';
import { CognitoException } from '../model/enum/cognito-exception';
import { DocType } from '../model/doc-type';
import { LanguageEnum } from '../model/enum/language-enum';
import { jwtDecode } from 'jwt-decode';
import { Observable, of, throwError, firstValueFrom, EMPTY } from 'rxjs';
import { tap, catchError, shareReplay } from 'rxjs';
import { UserResponseDto } from '../model/response/UserResponseDto';
import { enviroment } from 'src/environments/environment';
import { LoginResponse, GetUserResponse } from '../model/response/auth-response';
import { SessionPersistenceService } from './session-persistence.service';
import { clearBrowserSessionMarker } from '../shared/session-cookie.util';
import { readAndStripHandoffCode, stripUrlFragment } from '../shared/desktop-handoff.util';
import { AccessBlockService } from './access-block.service';
import { AccountDeletionService, PENDING_DELETION_ROUTE } from './account-deletion.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  user: User = new User();
  private refreshTimer: any;
  private loggingOut = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private readonly HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;
  private readonly heartbeatUrl = enviroment.BASE_URL_API + '/user/heartbeat';
  private visibilityHandler: (() => void) | null = null;
  
  // Cache de dados do usuário
  private userDataCache: UserResponseDto | null = null;
  private userDataCache$: Observable<UserResponseDto> | null = null;

  // Cache de validação do token (evita chamadas HTTP repetidas na navegação)
  private tokenValidationCache: { token: string; timestamp: number } | null = null;
  private readonly CACHE_TTL = 60000; // 60 segundos

  constructor(
    private router: Router,
    private cognitoService: CognitoService,
    private http: HttpClient,
    private sessionPersistence: SessionPersistenceService,
    private accessBlockService: AccessBlockService,
    private accountDeletion: AccountDeletionService
  ) {}

  /**
   * Guarda a URL de retorno (path completo) para navegar até ela após o login.
   * O fragmento é descartado: é onde o handoff do app desktop carrega os tokens, e esta
   * chave vai pro localStorage e só é removida ao ser consumida — se o usuário desistir
   * no /login, uma credencial no fragmento ficaria gravada em disco indefinidamente.
   */
  setPostLoginRedirect(url: string): void {
    if (url) localStorage.setItem('postLoginRedirect', stripUrlFragment(url));
  }

  /** Lê e remove a URL de retorno guardada por setPostLoginRedirect. */
  consumePostLoginRedirect(): string | null {
    const url = localStorage.getItem('postLoginRedirect');
    if (url) localStorage.removeItem('postLoginRedirect');
    return url;
  }

  /**
   * Chamado no boot (APP_INITIALIZER): hidrata a sessão (localStorage → sessionStorage)
   * e, se o token estiver perto de expirar, renova ANTES de o app subir — evita que uma
   * aba nova dispare chamadas (ex.: /user/active) com um idToken stale (erro "email não
   * identificado no token", que só some no F5 depois do refresh). Nunca lança.
   */
  async bootstrapSession(): Promise<void> {
    try {
      // A hidratação (localStorage → sessionStorage) já roda no main.ts, antes do Angular subir.
      //
      // O handoff do desktop vem primeiro: ele instala a sessão que o ensureFreshOnBootstrap
      // logo abaixo espera encontrar no sessionStorage. Precisa ser sequencial aqui dentro, e
      // não um APP_INITIALIZER separado: initializers que devolvem promise rodam em PARALELO
      // (o Angular junta tudo num Promise.all), então os dois disputariam o sessionStorage.
      await this.consumeHandoffCode();

      // Garante tokens frescos (renova se o idToken estiver stale) antes do uso.
      await this.ensureFreshOnBootstrap();
    } catch (error) {
      console.error('Erro no bootstrap da sessão:', error);
    }
  }

  /**
   * Handoff do app desktop (Vision): troca o código de uso único da URL (?hc=) pelos tokens
   * da sessão e a instala, para o AuthGuard não mandar o usuário pro /login.
   *
   * Roda no boot, antes do Router — ver bootstrapSession(). Nunca lança: qualquer falha cai
   * no fluxo de login normal.
   *
   * Contrato: ftcloud-api/docs/integracoes/HANDOFF_DESKTOP_WEB_ONETIME_CODE.md
   */
  private async consumeHandoffCode(): Promise<void> {
    const code = readAndStripHandoffCode();
    if (!code) return;

    try {
      const response = await firstValueFrom(this.cognitoService.exchangeHandoffCode(code));
      const authResult = response.authenticationResult;
      if (!authResult?.accessToken || !authResult?.idToken) return;

      // Sessão de OUTRO usuário já aberta neste navegador: descarta tudo antes de instalar.
      // Sobrescrever só os tokens deixaria userID/nameUser/_ui_role do usuário anterior — e a
      // compra poderia ser atribuída à conta errada. Seguro limpar agora: a troca já
      // respondeu e os tokens novos estão em memória.
      if (this.isForeignUser(authResult.idToken)) {
        sessionStorage.clear();
        this.sessionPersistence.clear();
        this.clearUserCache();
      }

      this.cognitoService.applyAuthenticationResult(response);

      // Waitlist: a troca já devolve o approvalStatus, então o shell pode bloquear no boot
      // em vez de esperar a primeira volta do /user/active — sem isso, um usuário pendente
      // veria a tela de perfil por um instante antes do bloqueio aparecer.
      this.accessBlockService.setFromStatus(response.approvalStatus);

      // nameUser é exigido por refreshAuth(refreshToken, username): sem ele a primeira
      // renovação iria com string vazia e falharia. O /user/active depois sobrescreve com o
      // valor real.
      const username = this.usernameFromTokens(authResult.accessToken, authResult.idToken);
      if (username) sessionStorage.setItem('nameUser', username);

      this.sessionPersistence.persist();
    } catch {
      // Código inválido, expirado, já consumido, ou sessão do desktop encerrada — a API
      // devolve o mesmo erro genérico nos quatro casos, de propósito. Nada a fazer: sem
      // sessão instalada, o AuthGuard manda pro /login guardando a rota em
      // postLoginRedirect, e o usuário volta pra ela após autenticar (§10 do contrato).
    }
  }

  /** true se já há sessão no navegador e ela pertence a outro usuário que não o do handoff. */
  private isForeignUser(incomingIdToken: string): boolean {
    const incoming = this.decodeToken(incomingIdToken)?.['custom:dbuser_id'];
    const existing = this.getUserInformations();
    return !!existing && !!incoming && String(existing) !== String(incoming);
  }

  /** accessToken traz `username`; idToken traz `cognito:username`. Email é o último recurso. */
  private usernameFromTokens(accessToken: string, idToken: string): string | null {
    const access = this.decodeToken(accessToken);
    const id = this.decodeToken(idToken);
    return access?.username ?? id?.['cognito:username'] ?? id?.email ?? null;
  }

  /**
   * Renova o token no boot se o accessToken OU o idToken estiverem ausentes/perto de
   * expirar, ou se o idToken (usado no /user/active) não tiver o claim de email.
   * Isso cobre a aba nova hidratada com um idToken stale/dessincronizado (erro
   * "email não identificado no token", que só some no F5 depois de um refresh).
   */
  private async ensureFreshOnBootstrap(): Promise<void> {
    const token = sessionStorage.getItem('token');
    const idToken = sessionStorage.getItem('idToken');
    const refreshToken = sessionStorage.getItem('refreshToken');
    if (!token || !refreshToken) return;

    const stale =
      this.tokenNeedsRefresh(token) ||
      this.tokenNeedsRefresh(idToken) ||
      !this.tokenHasEmail(idToken);
    if (!stale) return; // accessToken e idToken frescos, idToken com email → nada a fazer

    // Se o idToken é inválido (sem email/valor errado), remove-o ANTES do refresh: o
    // interceptor anexa o idToken como Bearer também no /global/refresh, e um idToken ruim
    // faz o próprio refresh falhar (401). Sem ele, o refresh usa só o refreshToken (body).
    if (!this.tokenHasEmail(idToken)) {
      sessionStorage.removeItem('idToken');
    }

    try {
      const username = sessionStorage.getItem('nameUser') || '';
      const response = await firstValueFrom(this.cognitoService.refreshAuth(refreshToken, username));
      if (response.authenticationResult?.accessToken) {
        // Tokens novos já gravados no sessionStorage pelo tap() do CognitoService.refreshAuth.
        this.sessionPersistence.persist();
      }
    } catch {
      // Refresh falhou (ex.: refreshToken expirado): limpa a sessão stale SEM navegar
      // (o Router ainda não está pronto no boot); o AuthGuard cuidará do redirect depois.
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      this.sessionPersistence.clear();
    }
  }

  /** true se o token está ausente ou a <=5min de expirar (ou sem exp). */
  private tokenNeedsRefresh(token: string | null): boolean {
    if (!token) return true;
    const decoded = this.decodeToken(token);
    const msLeft = decoded?.exp ? decoded.exp * 1000 - Date.now() : -1;
    return msLeft <= 300000;
  }

  /** true se o token decodifica e carrega o claim de email (exigido pelo /user/active). */
  private tokenHasEmail(token: string | null): boolean {
    if (!token) return false;
    const decoded = this.decodeToken(token);
    return !!(decoded && decoded.email);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    try {
      const token = sessionStorage.getItem('token');
      const refreshToken = sessionStorage.getItem('refreshToken');

      if (!token) return this.denyAndRedirectToLogin(state.url);

      // Conta em carência de exclusão: só a tela de desistência é acessível. Cobre deep link,
      // handoff do desktop (flag gravado no boot, antes do Router) e o chat do FT Pilot, que
      // roda em outro serviço e não devolve o 403 por conta própria.
      if (this.accountDeletion.isPending && state.url.split(/[?#]/)[0] !== PENDING_DELETION_ROUTE) {
        await this.router.navigate([PENDING_DELETION_ROUTE]);
        return false;
      }

      // 1) Cache: se já validou este token recentemente, permite navegação instantânea
      if (this.isTokenCacheValid(token)) {
        return true;
      }

      // 2) Verificação local do JWT: se exp > 5 min, permite sem chamada HTTP
      const decodedToken = this.decodeToken(token);
      if (decodedToken?.exp) {
        const timeUntilExpiry = (decodedToken.exp * 1000) - Date.now();
        if (timeUntilExpiry > 300000) { // mais de 5 minutos
          this.updateTokenCache(token);
          return true;
        }
      }

      // 3) Token perto de expirar ou sem exp: faz uma única chamada HTTP para validar
      const user = await this.verifyAndGetUser(token);
      if (user) {
        if (user.username) {
          sessionStorage.setItem('nameUser', user.username);
        }
        this.updateTokenCache(token);
        return true;
      }

      // 4) Fallback: tenta refresh se disponível
      if (refreshToken) {
        const refreshStatus = await this.refreshAuth();
        if (refreshStatus === CognitoException.SUCCESS) {
          const newToken = sessionStorage.getItem('token');
          if (newToken) {
            this.updateTokenCache(newToken);
          }
          return true;
        }
      }
      return this.denyAndRedirectToLogin(state.url);

    } catch (error) {
      console.error('Erro ao verificar token ou realizar refresh:', error);
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      this.clearUserCache();
      this.tokenValidationCache = null;
      return this.denyAndRedirectToLogin(state.url);
    }
  }

  /**
   * Nega a navegação e manda pro /login. Retornar `false` sozinho (sem navegar)
   * só cancela a navegação — se não havia nada renderizado antes (acesso direto
   * a uma rota conhecida, F5, etc.), a tela fica em branco em vez de ir pro login.
   * Guarda a URL de retorno para reabrir a mesma rota após autenticar.
   */
  private async denyAndRedirectToLogin(returnUrl: string): Promise<false> {
    this.setPostLoginRedirect(returnUrl);
    await this.router.navigate(['/login']);
    return false;
  }

  /**
   * Verifica se o cache de validação do token ainda é válido
   */
  private isTokenCacheValid(token: string): boolean {
    return !!(
      this.tokenValidationCache &&
      this.tokenValidationCache.token === token &&
      (Date.now() - this.tokenValidationCache.timestamp) < this.CACHE_TTL
    );
  }

  /**
   * Atualiza o cache de validação do token
   */
  private updateTokenCache(token: string): void {
    this.tokenValidationCache = { token, timestamp: Date.now() };
  }
  
  async signUp(account: Account, language: string | null) {
    account.email.trim();
    if(account.docNumber){
      account.docNumber = this.removeMask(account.docNumber);
      account.docType = (account.docNumber.length === 11 ? DocType.CPF : DocType.CNPJ);
    }else{
      account.docNumber = '0'.repeat(11);
      account.docType = DocType.CPF;      
    }
    account.language = (language === null ? LanguageEnum.EN_US : language);
    return firstValueFrom(this.cognitoService.signUp(account));
  }

  async confirmSignUp(email: string, code: string) {
    return firstValueFrom(this.cognitoService.confirmSignup(email, code));
  }

  async confirmLoginMFA(session: string, username: string, code: string): Promise<boolean> {
    if (!session) return false; 
    
    try {
      return false;
    } catch (error) {
      console.error('Erro ao verificar código MFA:', error);
      return false;
    }
  }
  
  async initiateAuth(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await firstValueFrom(this.cognitoService.login(email, password));
      this.startTokenRefresh();
      return response;
    } catch (error) {
      console.error('Erro ao autenticar:', error);
      throw error;
    }
  }
  

  async refreshAuth(): Promise<string> {
    const refreshToken = sessionStorage.getItem('refreshToken');
    const username = sessionStorage.getItem('nameUser') || '';
  
    if (!refreshToken) {
      console.error('RefreshToken não encontrado no sessionStorage.');
      return Promise.reject('RefreshToken ausente.');
    }
  
    try {
      const response = await firstValueFrom(this.cognitoService.refreshAuth(refreshToken, username));
  
      if (!response.authenticationResult?.accessToken) {
        console.error('Token vazio recebido ao tentar renovar.');
        return Promise.reject('Token vazio recebido ao tentar renovar.');
      }

      const newToken = response.authenticationResult.accessToken;
      // Tokens novos já gravados no sessionStorage pelo tap() do CognitoService.refreshAuth.
      this.sessionPersistence.persist();
      await this.getUser(newToken);

      this.startTokenRefresh();
      return CognitoException.SUCCESS;
  
    } catch (error) {
      console.error('Erro ao renovar o token:', error);
      this.logout();
      return Promise.reject('Erro ao renovar o token.');
    }
  }
  
  private decodeToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error('Erro ao decodificar o token:', error);
      return null;
    }
  }

  public startHeartbeat(): void {
    this.stopHeartbeat();
    const call = () => this.http.post(this.heartbeatUrl, null)
      .pipe(catchError(() => EMPTY))
      .subscribe();
    call();
    this.heartbeatTimer = setInterval(call, this.HEARTBEAT_INTERVAL_MS);

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') call();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  public stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  public startTokenRefresh() {
    const token = sessionStorage.getItem('token');

    if (!token) {
      console.warn('Nenhum token encontrado para configurar o refresh automático.');
      return;
    }
    this.getUserInformations();
    const decodedToken = this.decodeToken(token);
    if (!decodedToken || !decodedToken.exp) {
      console.warn('Token inválido ou sem campo de expiração.');
      return;
    }

    const expirationTime = decodedToken.exp * 1000;
    const refreshTime = expirationTime - Date.now() - 60000;

    if (refreshTime <= 0) {
      console.warn('Token já está expirado ou próximo do vencimento.');
      return;
    }

    this.refreshTimer = setTimeout(async () => {
      try {
        await this.refreshAuth();
        this.startTokenRefresh();
      } catch (error) {
        this.logout();
      }
    }, refreshTime);
  }

  public stopTokenRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      console.log('Timer de refresh cancelado.');
    }
    this.stopHeartbeat();
  }

  
  async forgotPassword(email: string) : Promise<any> {
    return firstValueFrom(this.cognitoService.forgotPassword(email));
  }

  async confirmForgotPassword(email: string, password: string, code: string) : Promise<any> {
    return firstValueFrom(this.cognitoService.confirmForgotPassword(email, password, code));
  }

  async resendConfirmationCode(email: string) : Promise<any> {
    return firstValueFrom(this.cognitoService.resendConfirmationCode(email));
  }

  async getUser(token: string): Promise<GetUserResponse> {
    return firstValueFrom(this.cognitoService.getUser(token));
  }

  async setUserMFA(mfaEnabled: boolean): Promise<any> {
    return firstValueFrom(this.cognitoService.setUserMFA(mfaEnabled));
  }

  async updateUserAttibutes(token: string, mfaEnabled: boolean): Promise<any> {
    // MFA agora é gerenciado pelo endpoint /auth/mfa
    return firstValueFrom(this.cognitoService.setUserMFA(mfaEnabled));
  }

  /**
   * Verifica o token e retorna os dados do usuário em uma única chamada HTTP.
   * Retorna null se o token for inválido.
   */
  async verifyAndGetUser(token: string): Promise<GetUserResponse | null> {
    try {
      return await firstValueFrom(this.cognitoService.getUser(token));
    } catch {
      return null;
    }
  }

  /**
   * Verifica se o token é válido (mantido para compatibilidade com dashboard e login).
   * Usa verifyAndGetUser internamente.
   */
  async verifyToken(token: string): Promise<boolean> {
    const user = await this.verifyAndGetUser(token);
    return user !== null;
  }

  async revokeToken(token: string) {
    try {
      await firstValueFrom(this.cognitoService.revokeAuth());
    } catch (error) {
      console.error('Erro ao revogar token:', error);
    }
  }

  async respondToChallenge(session: string, username: string, code: string): Promise<LoginResponse> {
    return firstValueFrom(this.cognitoService.respondToChallenge(session, username, code));
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<any> {
    return firstValueFrom(this.cognitoService.changePassword(oldPassword, newPassword));
  }

  async changeNewRequiredPass(session: string, username: string, newPassword: string): Promise<LoginResponse> {
    return firstValueFrom(this.cognitoService.changeNewRequiredPass(session, username, newPassword));
  }

  /**
   * Obtém dados do usuário com cache
   * - Primeira chamada: busca do backend e armazena em cache
   * - Chamadas subsequentes: retorna do cache
   * - forceRefresh: força nova busca do backend
   */
  getUserData(forceRefresh: boolean = false): Observable<UserResponseDto> {
    // Se já tem cache e não é refresh forçado, retorna cache
    if (this.userDataCache && !forceRefresh) {
      return of(this.userDataCache);
    }

    // Se já tem uma requisição em andamento, retorna ela
    if (this.userDataCache$ && !forceRefresh) {
      return this.userDataCache$;
    }

    // Faz a requisição e armazena em cache
    const accountUrl = enviroment.BASE_URL_API + '/user';
    this.userDataCache$ = this.http.get<UserResponseDto>(`${accountUrl}/active`).pipe(
      tap((data) => {
        this.userDataCache = data;
        this.userDataCache$ = null;
        
        // Salvar no sessionStorage
        if (data) {
          sessionStorage.setItem("nameUser", data.name);
          sessionStorage.setItem("language", data.language);
          sessionStorage.setItem("userID", data.userID);
        }
      }),
      catchError((error) => {
        this.userDataCache$ = null;
        return throwError(() => error);
      }),
      shareReplay(1) // Compartilha resultado entre subscribers
    );

    return this.userDataCache$;
  }

  /**
   * Registra no cache da sessão que o primeiro acesso já foi tratado. O backend é
   * atualizado à parte; sem isto, o cache seguia com firstAccess=true até o próximo
   * login e as boas-vindas reabriam a cada visita à Minha Garagem.
   */
  markFirstAccessDone(): void {
    if (this.userDataCache) {
      this.userDataCache = { ...this.userDataCache, firstAccess: false };
    }
  }

  /**
   * Limpa cache do usuário (chamar no logout ou quando dados mudarem)
   */
  clearUserCache(): void {
    this.userDataCache = null;
    this.userDataCache$ = null;
    this.tokenValidationCache = null;
  }

  /**
   * loginQueryParams: repassado pro /login (ex.: mensagem de sessão encerrada por
   * outro dispositivo). Guarda de reentrância: revokeToken() passa pelo mesmo
   * interceptor que pode disparar outro logout() (ex.: sessão já morta no
   * servidor) — sem o guard, isso reentraria infinitamente.
   */
  async logout(loginQueryParams?: Record<string, string>) : Promise<boolean> {
    if (this.loggingOut) return false;
    this.loggingOut = true;
    try {
      await this.clearLocalSession();
      this.router.navigate(['/login'], loginQueryParams ? { queryParams: loginQueryParams } : {});
      return true;
    } finally {
      this.loggingOut = false;
    }
  }

  /**
   * Encerra a sessão local SEM navegar — para telas que mostram uma mensagem final antes de
   * mandar a pessoa ao login (ex.: exclusão de conta agendada). Mesma guarda de reentrância
   * do logout().
   */
  async endLocalSession(): Promise<void> {
    if (this.loggingOut) return;
    this.loggingOut = true;
    try {
      await this.clearLocalSession();
    } finally {
      this.loggingOut = false;
    }
  }

  private async clearLocalSession(): Promise<void> {
    let token = sessionStorage.getItem('refreshToken');
    if(token) {
      await this.revokeToken(token);
    }
    sessionStorage.clear();
    this.sessionPersistence.clear();
    clearBrowserSessionMarker();
    this.clearUserCache(); // Limpa cache ao fazer logout
    this.stopTokenRefresh();
  }

  private removeMask(str: string) : string {
    str = str.replaceAll(".","");
    str = str.replaceAll("-","");
    str = str.replaceAll("/","");
    return str;
  }

  public adminGetUser() {
    let token = sessionStorage.getItem("token");
    if(token) {
      const decodedToken = this.decodeToken(token);
      return decodedToken;
    }
  }

  public getAdminRole(): 'super_admin' | 'support' | 'operator' | null {
    const role = sessionStorage.getItem('_ui_role');
    if (role === 'super_admin' || role === 'support' || role === 'operator') return role;
    return null;
  }

  public getUserEmail(): string | null {
    const idToken = sessionStorage.getItem('idToken');
    if (idToken) {
      const decoded = this.decodeToken(idToken);
      if (decoded?.email) return decoded.email;
    }
    const token = sessionStorage.getItem('token');
    if (token) {
      const decoded = this.decodeToken(token);
      if (decoded?.email) return decoded.email;
    }
    return null;
  }

  public getUserInformations() {
    let token = sessionStorage.getItem("token");
    if (token) {
      const decodedToken = this.decodeToken(token);
      const userId = decodedToken?.['custom:dbuser_id'];
      if (userId) return userId;
    }
    // OAuth/social login: custom attributes are only in idToken, not accessToken
    let idToken = sessionStorage.getItem("idToken");
    if (idToken) {
      const decodedIdToken = this.decodeToken(idToken);
      return decodedIdToken?.['custom:dbuser_id'];
    }
  }
}



export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> => {
  return inject(AuthService).canActivate(next, state);
}
